import { useRef, useEffect, useState } from 'react'
import { BellIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { useNotificationStore, type NotificationType } from '@/stores/notificationStore'

interface NotificationDropdownProps {
  collapsed: boolean
}

const typeStyles: Record<NotificationType, string> = {
  success: 'border-l-green-500',
  error: 'border-l-red-500',
  warning: 'border-l-yellow-500',
  info: 'border-l-blue-500',
}

const typeIcons: Record<NotificationType, string> = {
  success: '\u2713',
  error: '\u2715',
  warning: '\u26a0',
  info: '\u2139',
}

export function NotificationDropdown({ collapsed }: NotificationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    notifications,
    unreadCount,
    dismissNotification,
    clearAllNotifications,
    markAllAsRead
  } = useNotificationStore()

  const handleToggle = () => {
    setIsOpen(prev => !prev)
    if (!isOpen && unreadCount > 0) {
      markAllAsRead()
    }
  }

  // Click outside detection
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Escape key handling
  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
    }
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isOpen])

  const formatTimestamp = (timestamp: number) => {
    const diff = Date.now() - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Notification Bell Button */}
      <button
        type="button"
        onClick={handleToggle}
        data-testid="notification-dropdown-trigger"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={clsx(
          'flex items-center rounded-lg transition-colors relative',
          collapsed
            ? 'w-full justify-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700'
            : 'w-full px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700',
          isOpen && 'bg-gray-100 dark:bg-gray-700'
        )}
      >
        <BellIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className={clsx(
              'absolute flex items-center justify-center rounded-full bg-red-500 text-white font-bold',
              collapsed
                ? 'top-0.5 right-2 h-4 min-w-4 px-1 text-[10px]'
                : 'top-1 left-5 h-4 min-w-4 px-1 text-[10px]'
            )}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        {!collapsed && (
          <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
            Notifications
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          data-testid="notification-dropdown-menu"
          className={clsx(
            'absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50',
            collapsed
              ? 'left-full bottom-0 ml-2 w-96'
              : 'left-0 bottom-full mb-2 w-96'
          )}
          role="menu"
          aria-orientation="vertical"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={clearAllNotifications}
                data-testid="notification-clear-all"
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                Clear all
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div
                data-testid="notification-empty-state"
                className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No notifications
              </div>
            ) : (
              <div className="py-1">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    data-testid="notification-item"
                    className={clsx(
                      'flex items-start gap-3 px-4 py-3 border-l-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
                      typeStyles[notification.type],
                      !notification.read && 'bg-gray-50 dark:bg-gray-700/30'
                    )}
                  >
                    {/* Type Icon */}
                    <span className={clsx(
                      'flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold',
                      notification.type === 'success' && 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
                      notification.type === 'error' && 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
                      notification.type === 'warning' && 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
                      notification.type === 'info' && 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
                    )}>
                      {typeIcons[notification.type]}
                    </span>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white break-words">
                        {notification.title}
                      </p>
                      {notification.message && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {formatTimestamp(notification.timestamp)}
                      </p>
                    </div>

                    {/* Dismiss Button */}
                    <button
                      type="button"
                      onClick={() => dismissNotification(notification.id)}
                      data-testid="notification-dismiss"
                      className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      aria-label="Dismiss notification"
                    >
                      <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
